/**
 * Converts CLI task descriptions and comments to sanitized HTML for the Hypertask API.
 * Detects existing HTML vs Markdown vs plain text; Markdown is compiled with `marked` (GFM)
 * then cleaned with `sanitize-html`.
 */

import { marked } from 'marked';
import sanitizeHtml from 'sanitize-html';

marked.use({
  gfm: true,
  breaks: true,
});

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

/** Heuristics that suggest Markdown (avoid patterns that match plain prose, e.g. `2 * 3 * 4`). */
const MARKDOWN_HINTS: RegExp[] = [
  /^\s{0,3}#{1,6}\s/m,
  /^\s{0,3}(?:[-*+]|\d{1,9}\.)\s/m,
  /^\s{0,3}>\s/m,
  /```/,
  /\[([^\]]+)\]\(([^)]+)\)/,
  /!\[([^\]]*)\]\(([^)]+)\)/,
  /\*\*[^*\n]+?\*\*/,
  /__[^_\n]+?__/,
  /~~[^~]+?~~/,
  /^\s*---\s*$/m,
  /(?:^|\n)\s*\|[^\n]+\|[^\n]*\|/m,
];

function looksLikeMarkdown(text: string): boolean {
  return MARKDOWN_HINTS.some((re) => {
    re.lastIndex = 0;
    return re.test(text);
  });
}

function hasProbableHtml(text: string): boolean {
  return HTML_TAG_RE.test(text.trim());
}

function extractBodyInner(html: string): string {
  const trimmed = html.trim();
  const m = trimmed.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (m?.[1]) return m[1].trim();
  return trimmed;
}

function sanitizeFragment(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img', 'del', 'strike'],
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      a: ['href', 'name', 'target', 'rel', 'title'],
      img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading', 'dataalign', 'media-type'],
      code: ['class'],
      pre: ['class'],
      td: ['colspan', 'rowspan'],
      th: ['colspan', 'rowspan'],
      span: ['class', 'data-type', 'data-id', 'data-label', 'uniqueindex', 'projectid'],
      p: ['class'],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowProtocolRelative: false,
    parseStyleAttributes: false,
  });
}

function plainTextToFragment(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped.replace(/\n/g, '<br>')}</p>`;
}

function markdownToFragment(text: string): string {
  const raw = marked(text, { async: false }) as string;
  return sanitizeFragment(raw.trim());
}

function userHtmlToFragment(text: string): string {
  return sanitizeFragment(extractBodyInner(text));
}

/**
 * HTML fragment suitable for task create/update `description` (matches prior CLI behavior: fragment only).
 */
export function cliDescriptionToHtml(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return trimmed;

  if (hasProbableHtml(trimmed)) {
    return userHtmlToFragment(trimmed);
  }
  if (looksLikeMarkdown(trimmed)) {
    return markdownToFragment(trimmed);
  }
  return plainTextToFragment(trimmed);
}

/**
 * Converts HTML from the API into readable plain text for CLI display.
 */
export function htmlToPlainText(html: string): string {
  if (!html?.trim()) return '';

  return html
    .replace(/<li[^>]*>/gi, '\n• ')
    .replace(/<\/(p|div|h[1-6]|ul|ol|tr)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<(p|div|h[1-6]|ul|ol|tr|thead|tbody)[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const COMMENT_DOC_PREFIX = '<html><head></head><body>';
const COMMENT_DOC_SUFFIX = '</body></html>';

/**
 * Full HTML document for comment add/update (aligned with Hypertask-stored comment shape).
 */
export function cliCommentToHtml(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) {
    return COMMENT_DOC_PREFIX + COMMENT_DOC_SUFFIX;
  }

  let fragment: string;
  if (hasProbableHtml(trimmed)) {
    fragment = userHtmlToFragment(trimmed);
  } else if (looksLikeMarkdown(trimmed)) {
    fragment = markdownToFragment(trimmed);
  } else {
    fragment = plainTextToFragment(trimmed);
  }
  return `${COMMENT_DOC_PREFIX}${fragment}${COMMENT_DOC_SUFFIX}`;
}
